import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import firstSip from '../../../backend/src/config/firstSipOffer.json'
import api from '@/services/api'

const storageKey = 'banjinos.firstSipOffer'
const attributionKeys = ['qr', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content']
const cleanAttribution = input => Object.fromEntries(attributionKeys
  .filter(key => typeof input?.[key] === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(input[key]))
  .map(key => [key, input[key]]))

export const useOfferStore = defineStore('offer', () => {
  let requestVersion = 0
  let saved
  try { saved = JSON.parse(sessionStorage.getItem(storageKey)) } catch { /* Memory still works when storage is unavailable. */ }
  const active = ref(saved?.code === firstSip.code)
  const challenge = ref(active.value ? saved?.challenge || null : null)
  const verification = ref(active.value ? saved?.verification || null : null)
  const rewardToken = ref(active.value ? saved?.rewardToken || '' : '')
  const postUrl = ref(active.value ? saved?.postUrl || '' : '')
  const verifying = ref(false)
  const verificationError = ref('')
  const attribution = ref(active.value ? cleanAttribution(saved?.attribution) : {})
  const linkError = ref('')
  const label = `${firstSip.name} · ${firstSip.percent}%`
  // `active` means the shopper arrived through a First Sip link, so we invite
  // them to share. Only `unlocked` changes a price: until Instagram confirms
  // the post, they see and pay the regular price and can always check out.
  const unlocked = computed(() => active.value && !!verification.value?.verificationToken)
  const payload = computed(() => unlocked.value ? {
    code: firstSip.code, verificationToken: verification.value.verificationToken, attribution: attribution.value
  } : undefined)

  function persist() {
    try {
      if (active.value) sessionStorage.setItem(storageKey, JSON.stringify({ code: firstSip.code, attribution: attribution.value,
        challenge: challenge.value, verification: verification.value, postUrl: postUrl.value, rewardToken: rewardToken.value }))
      else sessionStorage.removeItem(storageKey)
    } catch { /* The in-memory offer remains usable. */ }
  }
  function capture(query, hash = '') {
    if (!Object.hasOwn(query, 'promo')) return
    if (query.promo !== firstSip.code) {
      clear()
      linkError.value = 'This offer link is not recognized. Scan the First Sip Society QR again, or keep shopping at regular prices.'
      return
    }
    active.value = true
    linkError.value = ''
    attribution.value = { ...attribution.value, ...cleanAttribution(query) }
    if (hash.startsWith('#claim=')) {
      const token = hash.slice(7)
      if (/^[a-f0-9-]{36}\.[A-Za-z0-9_-]{43}$/.test(token)) {
        if (token !== rewardToken.value) resetVerification()
        rewardToken.value = token
      } else linkError.value = 'This reward link is invalid. Open the link in your Instagram DM again.'
    }
    persist()
  }
  function clear() {
    active.value = false
    resetVerification()
    challenge.value = null
    postUrl.value = ''
    rewardToken.value = ''
    attribution.value = {}
    linkError.value = ''
    persist()
  }
  function resetVerification() {
    requestVersion++
    verifying.value = false
    verification.value = null
    verificationError.value = ''
    persist()
  }
  function isVerified(sessionId) {
    return !!verification.value?.verificationToken && verification.value.sessionId === sessionId && Date.parse(verification.value.expiresAt) > Date.now()
  }
  function setPostUrl(value) {
    postUrl.value = value
    resetVerification()
  }
  function failure(error) {
    verificationError.value = [error.response?.data?.error, error.response?.data?.remedy].filter(Boolean).join(' ')
      || 'We could not reach the shop. Your cart is saved; try verifying again in a minute.'
  }
  async function prepareVerification(sessionId) {
    if (!active.value || !sessionId || verifying.value || challenge.value?.sessionId === sessionId) return
    resetVerification()
    const request = requestVersion
    verifying.value = true
    try {
      const { data } = await api.shop.firstSip.challenge({ sessionId })
      if (!active.value || request !== requestVersion) return
      challenge.value = { ...data.data, sessionId }
      persist()
    } catch (error) { if (request === requestVersion) failure(error) }
    finally { if (request === requestVersion) verifying.value = false }
  }
  async function verifyPost(sessionId) {
    if (!active.value || !sessionId || verifying.value) return
    resetVerification()
    const request = requestVersion
    verifying.value = true
    try {
      const { data } = await api.shop.firstSip.verify({ sessionId, url: postUrl.value })
      if (!active.value || request !== requestVersion) return
      verification.value = { ...data.data, sessionId }
      persist()
    } catch (error) { if (request === requestVersion) failure(error) }
    finally { if (request === requestVersion) verifying.value = false }
  }
  let expiryTimer
  async function claimReward(sessionId) {
    if (!active.value || !rewardToken.value || !sessionId || verifying.value || isVerified(sessionId)) return
    resetVerification()
    const request = requestVersion
    verifying.value = true
    try {
      const { data } = await api.shop.firstSip.claim({ sessionId, rewardToken: rewardToken.value })
      if (!active.value || request !== requestVersion) return
      verification.value = { ...data.data, sessionId }
      persist()
    } catch (error) { if (request === requestVersion) failure(error) }
    finally { if (request === requestVersion) verifying.value = false }
  }
  watch(verification, value => {
    clearTimeout(expiryTimer)
    if (!value) return
    const remaining = Date.parse(value.expiresAt) - Date.now()
    if (!(remaining > 0)) return resetVerification()
    expiryTimer = setTimeout(resetVerification, Math.min(remaining, 7200000))
  }, { immediate: true })
  // Display estimates only. Checkout always re-prices the saved cart on the server.
  function price(value) {
    return unlocked.value ? Math.round(Number(value) * (100 - firstSip.percent)) / 100 : Number(value)
  }
  return { active, unlocked, challenge, verification, rewardToken, postUrl, verifying, verificationError, attribution, linkError, payload, label, firstSip,
    capture, clear, price, resetVerification, isVerified, setPostUrl, prepareVerification, verifyPost, claimReward }
})
